import { useEffect, useMemo, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { flushSync } from "react-dom";
import { Alert, Button, ChoiceControl, DataTable, DetailList, Dialog, Field, Tabs, buttonClassName } from "../../../ui";
import type { DataColumn, FieldElement } from "../../../ui";
import { UnsavedChangesGuard } from "../../../app/forms/UnsavedChangesGuard";
import type { Schemas } from "../../workspace/api.generated";
import { failure } from "../../workspace/behavior";
import { date } from "../../workspace/model";
import { PageHeader, Panel, RowLink, StateNotice, handleAdminLinkNavigation, statusBadge } from "../../workspace/shared";
import { GuidedActionButton } from "../../workspace/GuidedActionButton";
import type { ContentMutation } from "../queries";
import { contentFormSchema, toKstLocalDateTime, toUtcLocalDateTime } from "../schema";
import type { ContentFormValues, ContentStatus, ContentType } from "../schema";

type QueryState = "ready" | "loading" | "error";
type DetailState = "idle" | "ready" | "loading" | "error" | "denied" | "not-found";
type Editor = { readonly mode: "create" | "edit"; readonly initial: ContentFormValues; readonly values: ContentFormValues };
type Transition = { readonly kind: "select"; readonly id: number } | { readonly kind: "type"; readonly value: ContentType } | { readonly kind: "status"; readonly value: ContentStatus | "" } | { readonly kind: "new" } | { readonly kind: "cancel" };
type Action = "publish" | "archive" | "delete";

const typeLabels: Record<ContentType, string> = { NOTICE: "공지", FAQ: "FAQ", GUIDE: "이용가이드" };
const emptyForm = (contentType: ContentType): ContentFormValues => ({ contentType, categoryCode: "", title: "", content: "", pinned: false, displayStartedAt: "", displayEndedAt: "", attachmentFileAssetIds: [] });
const formFromContent = (content: Schemas["ContentPostView"]): ContentFormValues => ({
  contentType: content.contentType as ContentType,
  categoryCode: content.categoryCode ?? "",
  title: content.title,
  content: content.content,
  pinned: content.pinned,
  displayStartedAt: toKstLocalDateTime(content.displayStartedAt),
  displayEndedAt: toKstLocalDateTime(content.displayEndedAt),
  attachmentFileAssetIds: [...content.attachmentFileAssetIds],
});

function sameForm(left: ContentFormValues, right: ContentFormValues) {
  return JSON.stringify(left) === JSON.stringify(right);
}

export interface ContentViewProps {
  readonly onBannerTab?: () => void;
  readonly mode?: "combined" | "list" | "detail" | "create";
  readonly editing?: boolean;
  readonly type: ContentType;
  readonly status: ContentStatus | "";
  readonly contents?: readonly Schemas["ContentPostView"][];
  readonly listState?: QueryState;
  readonly selectedId?: number;
  readonly detail?: Schemas["ContentPostView"];
  readonly detailState?: DetailState;
  readonly canManage: boolean;
  readonly notice?: string;
  readonly recoveryError?: string;
  readonly onTypeChange: (type: ContentType) => void;
  readonly onStatusChange: (status: ContentStatus | "") => void;
  readonly onSelect: (id: number) => void;
  readonly onRetryList: () => void;
  readonly onRetryDetail: () => void;
  readonly onMutation: (input: ContentMutation) => Promise<unknown>;
  readonly onMutationComplete: (input: ContentMutation, result: unknown) => Promise<void>;
  readonly onUpload: (file: File) => Promise<Schemas["FileOperationResponse"]>;
  readonly onDownload: (fileAssetId: number) => Promise<void>;
  readonly detailHref?: (id: number) => string;
  readonly onOpenDetail?: (id: number) => void;
  readonly newHref?: string;
  readonly onOpenNew?: () => void;
  readonly backHref?: string;
  readonly backLabel?: string;
  readonly onBack?: () => void;
  readonly onStartEdit?: () => void;
  readonly onCancelEdit?: () => void;
  readonly onEditorTypeChange?: (type: ContentType) => void;
}

export function ContentView(props: ContentViewProps) {
  const mode = props.mode ?? "combined";
  const [editor, setEditor] = useState<Editor>();
  const [transition, setTransition] = useState<Transition>();
  const [action, setAction] = useState<Action>();
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [fileError, setFileError] = useState("");
  const [downloadingId, setDownloadingId] = useState<number>();
  const titleRef = useRef<FieldElement>(null);
  const contentRef = useRef<FieldElement>(null);
  const startRef = useRef<FieldElement>(null);
  const endRef = useRef<FieldElement>(null);
  const reasonRef = useRef<FieldElement>(null);
  const dirty = Boolean(editor && !sameForm(editor.values, editor.initial));

  useEffect(() => {
    if (mode === "create") {
      setEditor((current) => current?.mode === "create"
        ? current
        : { mode: "create", initial: emptyForm(props.type), values: emptyForm(props.type) });
      return;
    }
    if (mode === "detail" && props.editing && props.detail) {
      setEditor((current) => current?.mode === "edit"
        ? current
        : { mode: "edit", initial: formFromContent(props.detail!), values: formFromContent(props.detail!) });
      return;
    }
    if (mode === "detail" && !props.editing) setEditor(undefined);
  }, [mode, props.detail?.id, props.editing, props.type]);

  useEffect(() => {
    setAction(undefined);
    setActionError("");
    setDeleteReason("");
  }, [props.selectedId]);

  const columns = useMemo(() => [
    { key: "title", title: "제목", sticky: "start" as const, render: (row: Schemas["ContentPostView"]) => props.detailHref
      ? <RowLink href={props.detailHref(row.id)} triggerKey={`content:${row.id}:detail`} onNavigate={() => props.onOpenDetail?.(row.id)}>{row.title}</RowLink>
      : row.title },
    { key: "category", title: "분류", render: (row: Schemas["ContentPostView"]) => row.categoryCode || "—" },
    { key: "pinned", title: "고정", render: (row: Schemas["ContentPostView"]) => row.pinned ? "고정" : "일반" },
    { key: "status", title: "상태", render: (row: Schemas["ContentPostView"]) => statusBadge(row.status) },
    { key: "updated", title: "수정일시", render: (row: Schemas["ContentPostView"]) => date(row.updatedAt) },
  ] satisfies readonly DataColumn<Schemas["ContentPostView"]>[], [dirty, editor, props.detailHref, props.onOpenDetail]);

  const applyTransition = (next: Transition) => {
    setTransition(undefined);
    if (next.kind === "select") { setEditor(undefined); props.onSelect(next.id); }
    if (next.kind === "type") { setEditor(undefined); props.onTypeChange(next.value); }
    if (next.kind === "status") { setEditor(undefined); props.onStatusChange(next.value); }
    if (next.kind === "new") {
      if (props.onOpenNew) props.onOpenNew();
      else { const values = emptyForm(props.type); setEditor({ mode: "create", initial: values, values }); }
    }
    if (next.kind === "cancel") { setEditor(undefined); props.onCancelEdit?.(); }
    setErrors({}); setSaveError(""); setFileError("");
  };
  const requestTransition = (next: Transition) => dirty ? setTransition(next) : applyTransition(next);

  const update = <K extends keyof ContentFormValues>(key: K, value: ContentFormValues[K]) => {
    setEditor((current) => current ? { ...current, values: { ...current.values, [key]: value } } : current);
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const save = async () => {
    if (!editor || uploading || actionPending) return;
    setSaveError(""); setErrors({});
    const parsed = contentFormSchema.safeParse(editor.values);
    if (!parsed.success) {
      const nextErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) nextErrors[String(issue.path[0])] ??= issue.message;
      setErrors(nextErrors);
      const first = parsed.error.issues[0]?.path[0];
      ({ title: titleRef, content: contentRef, displayStartedAt: startRef, displayEndedAt: endRef } as Record<string, typeof titleRef>)[String(first)]?.current?.focus();
      return;
    }
    const body: Schemas["SaveContentRequest"] = {
      contentType: parsed.data.contentType,
      categoryCode: parsed.data.categoryCode || null,
      title: parsed.data.title.trim(),
      content: parsed.data.content.trim(),
      pinned: parsed.data.pinned,
      displayStartedAt: toUtcLocalDateTime(parsed.data.displayStartedAt),
      displayEndedAt: toUtcLocalDateTime(parsed.data.displayEndedAt),
      attachmentFileAssetIds: parsed.data.attachmentFileAssetIds,
    };
    const input: ContentMutation = editor.mode === "create" ? { kind: "create", body } : { kind: "update", id: props.selectedId!, body };
    setActionPending(true);
    try {
      const result = await props.onMutation(input);
      flushSync(() => {
        setEditor((current) => current ? { ...current, initial: current.values } : current);
      });
      await props.onMutationComplete(input, result);
      setEditor(undefined);
    } catch (cause) { setSaveError(failure(cause).message); }
    finally { setActionPending(false); }
  };

  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file || !editor || uploading) return;
    setUploading(true); setFileError("");
    try {
      const operation = await props.onUpload(file);
      if (!operation.fileAssetId) throw new Error("READY upload has no file ID.");
      if (editor.values.attachmentFileAssetIds.includes(operation.fileAssetId)) setFileError("이미 첨부된 파일입니다.");
      else update("attachmentFileAssetIds", [...editor.values.attachmentFileAssetIds, operation.fileAssetId]);
    } catch (cause) { setFileError(failure(cause).message); }
    finally { setUploading(false); }
  };

  const runAction = async () => {
    if (!action || !props.detail || actionPending) return;
    if (action === "delete" && !deleteReason.trim()) {
      setActionError("삭제 사유를 입력해 주세요."); reasonRef.current?.focus(); return;
    }
    const input: ContentMutation = action === "delete"
      ? { kind: "delete", id: props.detail.id, body: { reason: deleteReason.trim() } }
      : { kind: action, id: props.detail.id };
    setActionPending(true); setActionError("");
    try {
      const result = await props.onMutation(input);
      await props.onMutationComplete(input, result);
      setAction(undefined); setDeleteReason("");
    } catch (cause) { setActionError(failure(cause).message); }
    finally { setActionPending(false); }
  };

  const download = async (id: number) => {
    setDownloadingId(id); setFileError("");
    try { await props.onDownload(id); }
    catch (cause) { setFileError(failure(cause).message); }
    finally { setDownloadingId(undefined); }
  };

  const detailContent = props.detailState === "loading" ? <Alert persistent variant="information" title="콘텐츠 상세를 불러오는 중입니다">본문과 첨부파일을 확인하고 있습니다.</Alert>
    : props.detailState === "error" ? <Alert persistent variant="danger" title="콘텐츠 상세를 불러오지 못했습니다" action={<Button size="small" variant="quiet" onClick={props.onRetryDetail}>다시 조회</Button>}>목록은 유지됩니다. 상세만 다시 조회해 주세요.</Alert>
      : props.detailState === "denied" ? <StateNotice state="denied" />
        : props.detailState === "not-found" ? <Alert persistent variant="information" title="콘텐츠를 찾을 수 없습니다">목록을 갱신한 뒤 다시 선택해 주세요.</Alert>
        : props.detail ? <Panel title={props.detail.title} description={`${typeLabels[props.detail.contentType as ContentType] ?? props.detail.contentType} · 콘텐츠 번호 ${props.detail.id}`}>
          <DetailList items={[
            { label: "상태", value: statusBadge(props.detail.status) },
            { label: "분류", value: props.detail.categoryCode || "—" },
            { label: "고정", value: props.detail.pinned ? "고정함" : "고정하지 않음" },
            { label: "표시 기간", value: `${date(props.detail.displayStartedAt)} ~ ${date(props.detail.displayEndedAt)}` },
            { label: "게시일시", value: date(props.detail.publishedAt) },
            { label: "수정일시", value: date(props.detail.updatedAt) },
          ]} />
          <h3>본문</h3><p className="admin-pre">{props.detail.content}</p>
          <h3>첨부파일</h3>
          {props.detail.attachmentFileAssetIds.length ? <div className="admin-inline-actions">{props.detail.attachmentFileAssetIds.map((id) => <Button key={id} size="small" variant="quiet" isLoading={downloadingId === id} disabled={downloadingId !== undefined} onClick={() => void download(id)}>첨부파일 {id} 다운로드</Button>)}</div> : <p>첨부파일이 없습니다.</p>}
          {fileError ? <Alert persistent variant="danger" title="파일을 처리하지 못했습니다">{fileError}</Alert> : null}
          <div className="admin-detail-actions">
            {props.detail.status !== "ARCHIVED" ? <GuidedActionButton size="small" variant="danger" blockedReason={props.canManage ? null : "콘텐츠 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요."} onClick={() => setAction("delete")}>삭제</GuidedActionButton> : null}
            {props.detail.status !== "ARCHIVED" ? <GuidedActionButton size="small" variant="secondary" blockedReason={props.canManage ? null : "콘텐츠 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요."} onClick={() => setAction("archive")}>보관</GuidedActionButton> : null}
            {props.detail.status !== "PUBLISHED" && props.detail.status !== "ARCHIVED" ? <GuidedActionButton size="small" blockedReason={props.canManage ? null : "콘텐츠 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요."} onClick={() => setAction("publish")}>게시</GuidedActionButton> : null}
          </div>
        </Panel> : props.selectedId ? null : <Alert persistent variant="information" title="선택한 콘텐츠가 없습니다">목록에서 상세를 확인하거나 새 콘텐츠를 작성해 주세요.</Alert>;

  const editorContent = editor ? <Panel title={editor.mode === "create" ? "새 콘텐츠 작성" : "콘텐츠 수정"} description="임시 저장 후 게시 버튼으로 별도 발행합니다.">
    {saveError ? <Alert persistent variant="danger" title="콘텐츠를 저장하지 못했습니다">{saveError} 입력 내용은 그대로 유지됩니다.</Alert> : null}
    <form className="admin-command" noValidate onSubmit={(event) => { event.preventDefault(); void save(); }}>
      <Field compact control="select" label="콘텐츠 유형" required value={editor.values.contentType} onChange={(event) => { const next = event.currentTarget.value as ContentType; update("contentType", next); props.onEditorTypeChange?.(next); }}><option value="NOTICE">공지</option><option value="FAQ">FAQ</option><option value="GUIDE">이용가이드</option></Field>
      <Field compact label="분류 코드" maxLength={40} value={editor.values.categoryCode} error={errors.categoryCode} onChange={(event) => update("categoryCode", event.currentTarget.value)} />
      <Field ref={titleRef} compact label="제목" required maxLength={300} value={editor.values.title} error={errors.title} onChange={(event) => update("title", event.currentTarget.value)} />
      <Field ref={contentRef} compact control="textarea" label="본문" required value={editor.values.content} error={errors.content} onChange={(event) => update("content", event.currentTarget.value)} />
      <div className="admin-split">
        <Field ref={startRef} compact label="표시 시작일시 (KST)" type="datetime-local" value={editor.values.displayStartedAt} error={errors.displayStartedAt} onChange={(event) => update("displayStartedAt", event.currentTarget.value)} />
        <Field ref={endRef} compact label="표시 종료일시 (KST)" type="datetime-local" value={editor.values.displayEndedAt} error={errors.displayEndedAt} onChange={(event) => update("displayEndedAt", event.currentTarget.value)} />
      </div>
      <ChoiceControl checked={editor.values.pinned} label="목록 상단에 고정" onChange={(event) => update("pinned", event.currentTarget.checked)} />
      <fieldset className="admin-command__fieldset"><legend>첨부파일</legend>
        <Field compact label="파일 선택" type="file" disabled={uploading} description="20MB 이하 파일을 검사 완료 후 연결합니다." onChange={upload} />
        {uploading ? <Alert persistent variant="information" title="첨부파일을 검사하는 중입니다">검사가 끝날 때까지 저장할 수 없습니다.</Alert> : null}
        {fileError ? <Alert persistent variant="danger" title="첨부파일을 등록하지 못했습니다">{fileError}</Alert> : null}
        {editor.values.attachmentFileAssetIds.length ? <div className="admin-inline-actions">{editor.values.attachmentFileAssetIds.map((id) => <Button key={id} size="small" variant="quiet" type="button" disabled={uploading} onClick={() => update("attachmentFileAssetIds", editor.values.attachmentFileAssetIds.filter((item) => item !== id))}>첨부파일 {id} 제거</Button>)}</div> : <p>연결된 첨부파일이 없습니다.</p>}
      </fieldset>
      <div className="admin-detail-actions"><Button size="small" variant="quiet" type="button" disabled={actionPending} onClick={() => requestTransition({ kind: "cancel" })}>작성 취소</Button><Button size="small" type="submit" isLoading={actionPending} disabled={uploading}>임시 저장</Button></div>
    </form>
  </Panel> : null;

  const transitionDialog = <Dialog open={Boolean(transition)} title="작성 중인 내용을 버릴까요?" description="저장하지 않은 제목, 본문, 노출 조건과 첨부파일 선택이 사라집니다." onClose={() => setTransition(undefined)} secondaryAction={<Button variant="quiet" onClick={() => setTransition(undefined)}>계속 작성</Button>} primaryAction={<Button variant="danger" onClick={() => transition && applyTransition(transition)}>내용 버리기</Button>}><p>필요한 내용이라면 먼저 임시 저장해 주세요.</p></Dialog>;
  const actionDialog = <Dialog open={Boolean(action)} title={action === "publish" ? "콘텐츠를 게시할까요?" : action === "archive" ? "콘텐츠를 보관할까요?" : "콘텐츠를 삭제할까요?"} description={props.detail ? `${props.detail.id} · ${props.detail.title}` : undefined} closeDisabled={actionPending} onClose={() => { setAction(undefined); setActionError(""); setDeleteReason(""); }} secondaryAction={<Button variant="quiet" disabled={actionPending} onClick={() => setAction(undefined)}>취소</Button>} primaryAction={<Button variant={action === "delete" ? "danger" : "primary"} isLoading={actionPending} onClick={() => void runAction()}>{action === "publish" ? "게시 요청" : action === "archive" ? "보관 요청" : "삭제 요청"}</Button>}>
    {actionError && (action !== "delete" || deleteReason.trim()) ? <Alert persistent variant="danger" title="요청을 처리하지 못했습니다">{actionError}</Alert> : null}
    {action === "delete" ? <Field ref={reasonRef} compact control="textarea" label="삭제 사유" required maxLength={500} value={deleteReason} error={!deleteReason.trim() && actionError ? actionError : undefined} onChange={(event) => { setDeleteReason(event.currentTarget.value); setActionError(""); }} /> : <p>완료 후 서버의 최신 목록과 상세 상태를 다시 조회합니다.</p>}
  </Dialog>;

  if (mode === "detail" || mode === "create") {
    const isCreate = mode === "create";
    const currentType = props.detail ? props.detail.contentType as ContentType : props.type;
    const currentId = props.detail?.id ?? props.selectedId;
    return <>
      <PageHeader
        id={isCreate ? "ADM-S28" : "ADM-S27"}
        title={isCreate ? `새 ${typeLabels[currentType]} 작성` : props.detail?.title ?? `콘텐츠 ${currentId ?? ""}`}
        description={isCreate ? "본문과 노출 조건을 입력해 임시 저장합니다. 저장만으로 게시되지는 않습니다." : props.editing ? "내용과 노출 조건을 수정하고 임시 저장합니다." : "콘텐츠 본문, 노출 조건, 첨부와 게시 상태를 확인합니다."}
        breadcrumbs={[
          { label: "콘텐츠 운영", href: `/contents?type=${currentType}` },
          { label: isCreate ? "새 콘텐츠" : "콘텐츠 상세", href: isCreate ? `/contents/new?type=${currentType}` : props.detailHref?.(currentId ?? 0) ?? "#" },
        ]}
        backAction={props.backHref && props.onBack ? { label: props.backLabel ?? "목록으로 돌아가기", href: props.backHref, onClick: props.onBack } : undefined}
        action={!isCreate && !props.editing && props.detail && props.detail.status !== "ARCHIVED" ? (
          <GuidedActionButton
            size="small"
            blockedReason={props.canManage ? null : "콘텐츠 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요."}
            onClick={() => {
              if (props.onStartEdit) props.onStartEdit();
              else {
                const values = formFromContent(props.detail!);
                setEditor({ mode: "edit", initial: values, values });
              }
            }}
          >
            수정
          </GuidedActionButton>
        ) : undefined}
      />
      {props.notice ? <Alert persistent variant="information" title="콘텐츠 처리 결과">{props.notice}</Alert> : null}
      {props.recoveryError ? <Alert persistent variant="danger" title="최신 상태를 확인하지 못했습니다">{props.recoveryError}</Alert> : null}
      {isCreate ? editorContent : editorContent ?? detailContent}
      <UnsavedChangesGuard active={dirty} description="저장하지 않은 제목, 본문, 노출 조건과 첨부파일 선택이 사라집니다." />
      {transitionDialog}
      {actionDialog}
    </>;
  }

  return <>
    <PageHeader id="ADM-S15" title="콘텐츠 운영" description="공지, FAQ와 이용가이드를 작성하고 실제 게시 상태와 노출 조건을 관리합니다." meta={<p aria-live="polite">현재 조건 {props.contents?.length.toLocaleString("ko-KR") ?? 0}건</p>} action={props.newHref ? <a className={buttonClassName({ size: "small" })} href={props.newHref} onClick={props.onOpenNew ? (event) => handleAdminLinkNavigation(event, props.onOpenNew!) : undefined}>새 콘텐츠</a> : <GuidedActionButton size="small" blockedReason={props.canManage ? null : "콘텐츠 관리 권한이 없습니다. 권한 관리자에게 요청해 주세요."} onClick={() => requestTransition({ kind: "new" })}>새 콘텐츠</GuidedActionButton>} />
    {props.notice ? <Alert persistent variant="information" title="콘텐츠 운영 결과">{props.notice}</Alert> : null}
    {props.recoveryError ? <Alert persistent variant="danger" title="최신 상태를 확인하지 못했습니다">{props.recoveryError}</Alert> : null}
    <Tabs value={props.type} onChange={(value) => value === "banners" ? props.onBannerTab?.() : requestTransition({ kind: "type", value: value as ContentType })} items={[...([
      ["NOTICE", "공지"], ["FAQ", "FAQ"], ["GUIDE", "이용가이드"],
    ] as const).map(([id, label]) => ({ id, label, content: <>
      <Panel title="목록 조건" description="서버가 제공하는 유형과 게시 상태 조건을 적용합니다."><Field compact control="select" label="게시 상태" value={props.status} onChange={(event) => requestTransition({ kind: "status", value: event.currentTarget.value as ContentStatus | "" })}><option value="">전체</option><option value="DRAFT">작성 중</option><option value="PUBLISHED">게시됨</option><option value="ARCHIVED">보관됨</option></Field></Panel>
      <DataTable caption={`${label} 목록`} columns={columns} rows={props.contents ?? []} rowKey={(row) => row.id} state={props.listState} onRetry={props.onRetryList} />
    </> })), ...(props.onBannerTab ? [{ id: "banners", label: "배너", content: null }] : [])]} />
    {mode === "combined" ? editorContent ?? detailContent : null}
    {transitionDialog}
    {actionDialog}
  </>;
}
